import { Type, Music, Trash2, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const GRAPHIC_TYPES = [
  { value: "super", label: "Super / Text Overlay" },
  { value: "disclaimer", label: "Disclaimer" },
  { value: "end_frame", label: "End Frame" },
];

export const RestripeToolPanel = ({
  selectedAspectRatio,
  graphics,
  selectedGraphic,
  dialogOpen,
  setDialogOpen,
  newGraphic,
  setNewGraphic,
  duration,
  uploading,
  audioInputRef,
  hasAudioTracks,
  onAddGraphic,
  onDeleteGraphic,
  onSelectGraphic,
  onSeekTo,
  onUpdatePosition,
  onUploadAudio,
}) => {
  return (
    <div className="lg:col-span-3 space-y-6" data-testid="tools-sidebar">
      {/* Add Elements */}
      <div>
        <h2 className="text-xs text-cyan-400/60 mb-3 tracking-widest uppercase">
          Add Elements
        </h2>
        <div className="space-y-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="w-full flex items-center gap-3 p-3 border border-cyan-500/10 hover:border-cyan-500/30 rounded-sm transition-all text-left hover:bg-cyan-500/[0.03]"
                data-testid="add-super-btn"
              >
                <Type className="w-4 h-4 text-cyan-400/40" />
                <span className="text-cyan-200/60 text-sm">Add Text / Super</span>
              </button>
            </DialogTrigger>
            <DialogContent
              className="bg-[#020a14] border-cyan-500/20 rounded-sm sm:max-w-md"
              aria-describedby="add-graphic-desc"
            >
              <DialogHeader>
                <DialogTitle className="font-['Rajdhani'] tracking-wide">
                  ADD GRAPHIC OVERLAY
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-cyan-200/30 -mt-2" id="add-graphic-desc">
                Scoped to{" "}
                <span className="text-cyan-400/60 font-mono">{selectedAspectRatio}</span>
              </p>
              <div className="space-y-4 mt-2">
                <div>
                  <Label className="text-sm text-cyan-300/60">Type</Label>
                  <Select
                    value={newGraphic.type}
                    onValueChange={(v) => setNewGraphic({ ...newGraphic, type: v })}
                  >
                    <SelectTrigger className="h-10 rounded-sm mt-2" data-testid="graphic-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#020a14] border-cyan-500/20 rounded-sm">
                      {GRAPHIC_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-cyan-300/60">Text Content</Label>
                  <Textarea
                    placeholder="Enter overlay text..."
                    value={newGraphic.content}
                    onChange={(e) => setNewGraphic({ ...newGraphic, content: e.target.value })}
                    className="rounded-sm mt-2"
                    data-testid="graphic-content-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-cyan-300/60">Start (s)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max={duration}
                      value={newGraphic.start_time}
                      onChange={(e) => setNewGraphic({ ...newGraphic, start_time: parseFloat(e.target.value) || 0 })}
                      className="h-10 rounded-sm mt-2"
                      data-testid="start-time-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-cyan-300/60">End (s)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max={duration}
                      value={newGraphic.end_time}
                      onChange={(e) => setNewGraphic({ ...newGraphic, end_time: parseFloat(e.target.value) || 5 })}
                      className="h-10 rounded-sm mt-2"
                      data-testid="end-time-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={onAddGraphic}
                  className="w-full h-10 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                  disabled={!newGraphic.content.trim()}
                  data-testid="add-graphic-submit"
                >
                  ADD OVERLAY
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <button
            onClick={() => audioInputRef.current?.click()}
            className="w-full flex items-center gap-3 p-3 border border-cyan-500/10 hover:border-cyan-500/30 rounded-sm transition-all text-left hover:bg-cyan-500/[0.03]"
            data-testid="add-audio-btn"
          >
            <Music className="w-4 h-4 text-cyan-400/40" />
            <div>
              <span className="text-cyan-200/60 text-sm block">
                {uploading ? "Uploading..." : "Replace Audio Track"}
              </span>
              {hasAudioTracks && (
                <span className="text-xs text-green-400/60">Audio replacement active</span>
              )}
            </div>
          </button>
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*,.wav,.mp3,.aac"
            onChange={onUploadAudio}
            className="hidden"
          />
        </div>
      </div>

      {/* Graphics List */}
      <div>
        <h2 className="text-xs text-cyan-400/60 mb-3 tracking-widest uppercase">
          Overlays — {selectedAspectRatio} ({graphics.length})
        </h2>
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
          {graphics.map((g) => (
            <div
              key={g.id}
              onClick={() => {
                onSelectGraphic(g);
                onSeekTo(g.start_time);
              }}
              className={`p-3 border rounded-sm cursor-pointer transition-all ${
                selectedGraphic?.id === g.id
                  ? "border-cyan-500/40 bg-cyan-500/[0.05]"
                  : "border-cyan-500/10 hover:border-cyan-500/20"
              }`}
              data-testid={`graphic-item-${g.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-cyan-200/60 truncate max-w-[160px]">
                    {g.content || g.type}
                  </p>
                  <p className="text-xs text-cyan-200/30 mt-0.5">
                    {g.start_time}s - {g.end_time}s
                    <span className="ml-2 text-cyan-400/30">
                      x:{g.position?.x ?? 50} y:{g.position?.y ?? 50}
                    </span>
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteGraphic(g.id);
                  }}
                  className="text-red-400/40 hover:text-red-400 transition-colors p-1"
                  data-testid={`delete-graphic-${g.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          {graphics.length === 0 && (
            <p className="text-sm text-cyan-200/20 py-4 text-center">
              No overlays for {selectedAspectRatio}
            </p>
          )}
        </div>
      </div>

      {/* Position Controls */}
      {selectedGraphic && (
        <div className="card-glass rounded-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs text-cyan-400/60 tracking-widest uppercase">Position</h3>
            <span className="text-[10px] text-cyan-200/25 flex items-center gap-1">
              <Move className="w-3 h-3" /> Drag on preview
            </span>
          </div>
          <div>
            <Label className="text-xs text-cyan-300/60">
              X: {selectedGraphic.position?.x || 50}%
            </Label>
            <Slider
              value={[selectedGraphic.position?.x || 50]}
              onValueChange={([v]) =>
                onUpdatePosition(selectedGraphic, { ...selectedGraphic.position, x: v })
              }
              max={100}
              className="mt-1"
              data-testid="position-x-slider"
            />
          </div>
          <div>
            <Label className="text-xs text-cyan-300/60">
              Y: {selectedGraphic.position?.y || 50}%
            </Label>
            <Slider
              value={[selectedGraphic.position?.y || 50]}
              onValueChange={([v]) =>
                onUpdatePosition(selectedGraphic, { ...selectedGraphic.position, y: v })
              }
              max={100}
              className="mt-1"
              data-testid="position-y-slider"
            />
          </div>
        </div>
      )}
    </div>
  );
};
